import { PasseriniAPI } from './apiService';
import type { Costo, CostoFormData, CostosResponse } from '@/types/costo';
import type { ParteItem, PdfExportado } from '@/presupuestos/types';
import { nombreArchivoDeDisposition } from '@/presupuestos/utils/nombreArchivo';

export interface CopiarPresupuestoResponse extends CostosResponse {
  copiados: number;
}

export interface ImportarProformaResponse extends CostosResponse {
  importados: number;
  presupuestoOrigen: { id: string; nombre: string };
}

export const costoService = {
  /**
   * Obtener todos los items de presupuesto de un trámite
   */
  getCostos: async (tramiteId: string): Promise<CostosResponse> => {
    const response = await PasseriniAPI.get(`/tramites/${tramiteId}/presupuesto-items`);
    return response.data;
  },

  /**
   * Crear nuevo item de presupuesto
   */
  crearCosto: async (
    tramiteId: string,
    data: CostoFormData
  ): Promise<Costo> => {
    const response = await PasseriniAPI.post(
      `/tramites/${tramiteId}/presupuesto-items`,
      {
        nombre: data.nombre,
        aclaracion: data.aclaracion ?? null,
        // La parte la define la caja desde la que se creó el item.
        parte: data.parte,
        monto: data.monto,
        moneda: data.moneda,
        tipo: data.tipo,
        ...(data.importe_real !== undefined && { importe_real: data.importe_real }),
        ...(data.bonificacion_tipo !== undefined && {
          bonificacion_tipo: data.bonificacion_tipo,
          bonificacion_valor: data.bonificacion_valor ?? null,
        }),
        // Sin la clave, el backend asigna el destino por defecto.
        ...(data.destino_pago_id !== undefined && { destino_pago_id: data.destino_pago_id }),
      }
    );
    return response.data;
  },

  /**
   * Actualizar item de presupuesto existente
   */
  actualizarCosto: async (
    tramiteId: string,
    costoId: string,
    data: Partial<CostoFormData>
  ): Promise<Costo> => {
    const requestData: Record<string, any> = {};

    if (data.nombre !== undefined) {
      requestData.nombre = data.nombre;
    }
    if (data.aclaracion !== undefined) {
      requestData.aclaracion = data.aclaracion;
    }
    if (data.orden !== undefined) {
      requestData.orden = data.orden;
    }
    if (data.monto !== undefined) {
      requestData.monto = data.monto;
    }
    if (data.moneda !== undefined) {
      requestData.moneda = data.moneda;
    }
    if (data.tipo !== undefined) {
      requestData.tipo = data.tipo;
    }
    if (data.importe_real !== undefined) {
      requestData.importe_real = data.importe_real;
    }
    if (data.bonificacion_tipo !== undefined) {
      requestData.bonificacion_tipo = data.bonificacion_tipo;
    }
    if (data.bonificacion_valor !== undefined) {
      requestData.bonificacion_valor = data.bonificacion_valor;
    }
    if (data.destino_pago_id !== undefined) {
      requestData.destino_pago_id = data.destino_pago_id;
    }

    const response = await PasseriniAPI.put(
      `/tramites/${tramiteId}/presupuesto-items/${costoId}`,
      requestData
    );
    return response.data;
  },

  /**
   * Eliminar item de presupuesto
   */
  eliminarCosto: async (tramiteId: string, costoId: string): Promise<void> => {
    await PasseriniAPI.delete(`/tramites/${tramiteId}/presupuesto-items/${costoId}`);
  },

  /**
   * Guarda el orden de impresión de los ítems de una caja: cada ítem toma su
   * posición en `ids`. El orden también se aplica al presupuesto importado.
   */
  reordenarCostos: async (tramiteId: string, ids: string[]): Promise<CostosResponse> => {
    const response = await PasseriniAPI.post(
      `/tramites/${tramiteId}/presupuesto-items/reordenar`,
      { items: ids }
    );
    return response.data;
  },

  /**
   * Copiar todos los items de presupuesto de un trámite origen al trámite destino (append)
   */
  copiarPresupuesto: async (
    targetTramiteId: string,
    sourceTramiteId: string
  ): Promise<CopiarPresupuestoResponse> => {
    const response = await PasseriniAPI.post(
      `/tramites/${targetTramiteId}/presupuesto-items/copy`,
      { sourceTramiteId }
    );
    return response.data;
  },

  /**
   * Importar un presupuesto independiente confirmado dentro de una escritura.
   *
   * Genera una copia vinculada: las altas, ediciones y bajas hechas en la
   * escritura se reflejan en el presupuesto importado (no al revés). Si la
   * escritura ya tiene items, el backend responde 409 hasta que se confirme el
   * reemplazo.
   */
  importarProforma: async (
    tramiteId: string,
    presupuestoProformaId: string,
    reemplazar = false
  ): Promise<ImportarProformaResponse> => {
    const response = await PasseriniAPI.post(
      `/tramites/${tramiteId}/presupuesto-items/importar-proforma`,
      { presupuestoProformaId, reemplazar }
    );
    return response.data;
  },

  /**
   * Corta el vínculo de la escritura con el presupuesto importado. Los ítems de
   * la escritura se conservan sin vínculo o se eliminan; el presupuesto no se
   * modifica, salvo que vuelve a estar disponible para importar.
   */
  desasociarProforma: async (
    tramiteId: string,
    eliminarItems: boolean
  ): Promise<CostosResponse> => {
    const response = await PasseriniAPI.post(
      `/tramites/${tramiteId}/presupuesto-items/desasociar-proforma`,
      { eliminarItems }
    );
    return response.data;
  },

  /**
   * Exporta el PDF de una parte con los items de la escritura. El encabezado sale
   * del presupuesto importado: sin uno, el backend responde 422.
   */
  exportarPresupuesto: async (tramiteId: string, parte: ParteItem): Promise<PdfExportado> => {
    const response = await PasseriniAPI.get(`/tramites/${tramiteId}/presupuesto-items/export`, {
      params: { parte },
      responseType: 'blob',
    });
    return {
      blob: response.data,
      nombreArchivo: nombreArchivoDeDisposition(response.headers['content-disposition']),
    };
  },

  /**
   * Obtener sugerencias de nombres de items (DISTINCT global, para autocompletado)
   */
  getNombresSugeridos: async (search: string): Promise<string[]> => {
    const response = await PasseriniAPI.get('/presupuesto-items/nombres', {
      params: { search },
    });
    return response.data.nombres;
  },
};
